import { Injectable } from '@angular/core';
import { PlatformNavigationItemService } from '@data/_generated-api/platform-api/services/platform-navigation-item.service';
import { NavigationItemService } from '@/app/platform/platform-data/services/types';
import { ErrorInformation } from '@shared/models/error/error-information';

import { err, ok } from '@common/result/Result';
import Morphism from 'morphism';
import { AsyncResult } from '@common/result/asyncResult';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';

@Injectable()
export class NavigationItemMapFacade implements NavigationItemService {
  constructor(private readonly apiService: PlatformNavigationItemService) {}

  async changeConfiguration(
    navigationItem: NavigationItemEntity
  ): AsyncResult<NavigationItemEntity, ErrorInformation<string>> {
    const result = await this.apiService
      .postNavigationitemsActionChangeconfiguration({
        body: {
          id: navigationItem.id,
          configurationJson: JSON.stringify(navigationItem.config)
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const i = Morphism.map(NavigationItemEntity, result.value);
    return ok(i);
  }

  async delete(model: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.apiService
      .deleteNavigationitemsId({ id: model.deleteKey })
      .toPromise();
    if (!result.isValid) {
      return err();
    }

    const navigationItem = Morphism.map(NavigationItemEntity, result.value);
    return ok(navigationItem);
  }

  async move(
    navigationItem: NavigationItemEntity
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.apiService
      .postNavigationitemsActionMovemenuitem({
        body: {
          indexAtNewLocation: navigationItem.order,
          navigationItemId: navigationItem.id,
          newNavigationItemParentId: navigationItem.parentId
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }
    const item = Morphism.map(NavigationItemEntity, result.value);
    return ok(item);
  }

  async reOrder(
    navigationItem: NavigationItemEntity
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.apiService
      .postNavigationitemsActionReorder({
        body: {
          id: navigationItem.id,
          newIndex: navigationItem.order
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err([]);
    }

    const items = Morphism.map(NavigationItemEntity, result.value);
    return ok(items[0]);
  }

  async create(model: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.apiService
      .postNavigationitems({ body: model.creationModel })
      .toPromise();
    if (!result.isValid) {
      return err([]);
    }

    const item = Morphism.map(NavigationItemEntity, result.value);

    return ok(item);
  }

  async createMenuItem(
    navigationItem: NavigationItemEntity
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.apiService
      .postNavigationitemsActionCreatemenuitem({
        body: {
          appId: navigationItem.appId,
          navigationItemParentId: navigationItem.parentId,
          navigationItemName: navigationItem.name
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err([]);
    }

    const item = Morphism.map(NavigationItemEntity, result.value);
    return ok(item);
  }

  async changeName({
    id,
    name
  }: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const result = await this.apiService
      .postNavigationitemsActionChangename({
        body: {
          id,
          name
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const item = Morphism.map(NavigationItemEntity, result.value);
    return ok(item);
  }
}
